// What the trace screens read off a run's span tree (`recorded.ts` builds it from the
// recorded trace): its id and cost, the tree walked flat, per-trace analytics, dead air,
// effects, the JSON export, a generation's curl, and how one step relates to the others.
import { CalendarClock, MessageSquare } from "lucide-react";

import { effectOf, effectVerb, kindOf } from "@/shared/lib/trace-kinds";

import type { LucideIcon } from "lucide-react";

import type {
  Agent, Run,
} from "@/shared/types";
import type {
  AssistantPayload, ChatMessage, DeadAirGap, EffectEntry, ErrorObservation, ExportedTrace, FlatSpan, LlmInputPayload, RunKind, Session, Span,
  SpanIo, TokenUsage, ToolDef, ToolInputPayload, ToolUseBlock, TraceAnalytics,
} from "./types";

// A run's trace id is its own id.
export const traceId = (run: Run): string => run.id;

/* ------------------------------ tool catalog ------------------------------ */

// name → [description, params]; params value is the JSON-schema type.
const TOOL_CATALOG: Record<string, [description: string, params: Record<string, string>]> = {
  "web.search": ["Search the web and return the top sources for a query.", { q: "string" }],
  "file.read": ["Read an attached or workspace file and extract its text.", { file: "string", pages: "string" }],
  "memory.recall": ["Retrieve relevant long-term memories for the current task.", { query: "string", scope: "string" }],
  "artifact.create": ["Create a rich artifact (document, page or asset) in the workspace.", { title: "string", type: "string" }],
  "artifact.edit": ["Edit sections of an existing artifact.", { artifact: "string", sections: "array" }],
  "gmail.draft": ["Compose an email draft in the connected Gmail account.", { to: "string", subject: "string", body: "string" }],
  "gmail.send": ["Send an email through the connected Gmail account.", { draft: "string" }],
  "notion.create_page": ["Create a page in the connected Notion workspace.", { database: "string", title: "string" }],
  "airtable.append": ["Append rows to a table in the connected Airtable base.", { base: "string", table: "string", rows: "number" }],
  "drive.read": ["Read a file from the connected Google Drive.", { file: "string" }],
  "slack.post": ["Post a message to a Slack channel or DM.", { channel: "string", text: "string" }],
  "gcalendar.create": ["Create an event in the connected Google Calendar.", { title: "string", start: "string", end: "string" }],
};

export function toolDef(name: string): ToolDef {
  const [description, params] = TOOL_CATALOG[name] || [`Invoke the ${name} tool.`, { input: "string" }];
  return {
    name,
    description,
    input_schema: {
      type: "object",
      properties: Object.fromEntries(Object.entries(params).map(([k, v]) => [k, { type: v }])),
      required: Object.keys(params),
    },
  };
}

/* What the runtime priced the run at, its attempts added up. */
export const traceCost = (run: Run): number => run.recorded?.cost_usd ?? 0;

export function flatten(span: Span, depth = 0, out: FlatSpan[] = []): FlatSpan[] {
  out.push({ span, depth });
  for (const c of span.children) flatten(c, depth + 1, out);
  return out;
}

export function findSpan(span: Span, id: string): Span | null {
  if (span.id === id) return span;
  for (const c of span.children) {
    const hit = findSpan(c, id);
    if (hit) return hit;
  }
  return null;
}

export const SOURCE_META: Record<RunKind, { label: string; Icon: LucideIcon }> = {
  chat: { label: "Chat", Icon: MessageSquare },
  task: { label: "Task", Icon: CalendarClock },
};

/* ------------------------- per-trace analytics ---------------------------- */

// Time/cost/token breakdown across the whole span tree — llm vs tool wall time,
// per-turn cost, generation and tool counts.
export function traceAnalytics(root: Span): TraceAnalytics {
  const rows = flatten(root).map((r) => r.span);
  const llm = rows.filter((s) => s.kind === "llm");
  const tools = rows.filter((s) => s.kind === "tool");
  const llmMs = llm.reduce((n, s) => n + s.dur, 0);
  const toolMs = tools.reduce((n, s) => n + s.dur, 0);
  // a generation still running has no output yet
  const tokens = llm.reduce(
    (a, s) => ({
      input: a.input + (s.io.output?.usage?.input_tokens || 0),
      output: a.output + (s.io.output?.usage?.output_tokens || 0),
      cached: a.cached + (s.io.output?.usage?.cache_read_input_tokens || 0),
    }),
    { input: 0, output: 0, cached: 0 }
  );
  const turns = rows.filter((s) => s.kind === "turn");
  const perTurn = (turns.length ? turns : [root]).map((t) => ({
    name: t.name,
    cost: flatten(t).reduce((n, r) => n + (r.span.cost || 0), 0),
  }));
  const cost = llm.reduce((n, s) => n + (s.cost || 0), 0);
  return { llmMs, toolMs, otherMs: Math.max(0, root.dur - llmMs - toolMs), llmCount: llm.length, toolCount: tools.length, tokens, cost, perTurn, errors: rows.filter((s) => s.status === "error" && s.children.length === 0).length };
}

/* -------------------------------- dead air -------------------------------- */

/* Wall-clock the run spent doing nothing measurable — queueing, backoff after a
   rate limit, or a human not answering an approval. A waterfall that draws only
   the bars implies the gaps between them are instantaneous; they are usually
   where the time actually went. */
export function deadAir(root: Span, minMs = 400): DeadAirGap[] {
  const leaves = flatten(root)
    .map((r) => r.span)
    .filter((s) => s.children.length === 0)
    .sort((a, b) => a.start - b.start);
  const gaps: DeadAirGap[] = [];
  let cursor = 0;
  let prev: Span | null = null;
  for (const s of leaves) {
    if (s.start - cursor >= minMs) gaps.push({ start: cursor, dur: s.start - cursor, after: prev, before: s });
    cursor = Math.max(cursor, s.start + s.dur);
    prev = s;
  }
  if (root.dur - cursor >= minMs) gaps.push({ start: cursor, dur: root.dur - cursor, after: prev, before: null });
  return gaps;
}

export const totalDeadAir = (root: Span, minMs?: number): number => deadAir(root, minMs).reduce((n, g) => n + g.dur, 0);

/* ------------------------------ effect ledger ------------------------------ */

/* Everything the run changed outside itself: mail sent, rows written, messages
   posted, files edited. This is the only durable record the user has of an
   agent's side effects, and it is deliberately separate from the step tree —
   "what did it do" and "what did it touch" are different questions, and the
   second one is the one people ask after the fact.

   Reads are excluded on purpose: a ledger that lists a search alongside a sent
   email trains people to skim it. */
export function effectLedger(root: Span): EffectEntry[] {
  return (flatten(root)
    .map((r) => r.span)
    // `stepStatus` already folds skipped into ok, so this never drops a span
    // today; kept as written rather than quietly deleted.
    .filter((s) => (s.status as string) !== "skipped")
    .map((s): EffectEntry | null => {
      const tool = s.raw?.tool;
      const fromTool = s.kind === "tool" && effectOf(tool);
      if (fromTool) {
        return {
          span: s, at: s.start, status: s.status, tool,
          verb: effectVerb(fromTool, s.status), severity: fromTool.severity, undo: fromTool.undo,
          target: s.io?.input?.arguments ? summarizeArgs(s.io.input.arguments) : s.label,
          detail: s.status === "error" ? s.io?.output?.error : s.io?.output?.result,
        };
      }
      if (kindOf(s.kind).effect) {
        return {
          span: s, at: s.start, status: s.status, tool: s.kind,
          verb: s.kind === "edit" ? "Edited file" : "Called webhook",
          severity: s.kind === "edit" ? "medium" : "high",
          undo: s.kind === "edit" ? "Revert patch" : null,
          target: s.name,
          detail: s.kind === "edit" ? `+${s.io?.output?.added ?? 0} −${s.io?.output?.removed ?? 0}` : String(s.io?.output?.status ?? ""),
        };
      }
      return null;
    })
    .filter(Boolean) as EffectEntry[])
    .sort((a, b) => a.at - b.at);
}

// First non-trivial argument value — enough to identify the target of an action.
function summarizeArgs(args: Record<string, unknown> | undefined): string | null {
  const entries = Object.entries(args || {});
  const pick = entries.find(([k]) => ["to", "channel", "file", "title", "table", "database", "draft"].includes(k)) || entries[0];
  if (!pick) return null;
  const [, v] = pick;
  return typeof v === "string" ? v : JSON.stringify(v);
}

/* ----------------------------- export / curl ------------------------------ */

/** `root` is the tree on screen. */
export function exportTrace(run: Run, agent: Agent, root: Span): ExportedTrace {
  return {
    trace_id: traceId(run),
    name: run.title,
    agent: agent.id,
    source: run.source,
    status: run.status,
    duration_ms: root.dur,
    observations: flatten(root).map(({ span, depth }) => ({
      ...span.meta,
      name: span.name,
      depth,
      input: span.io.input,
      output: span.io.output,
    })),
  };
}

export function curlForGeneration(io: SpanIo): string {
  const body = {
    model: io.input.model,
    // a setting the run did not record is left to the model's default, not sent as null
    ...(io.input.max_tokens != null ? { max_tokens: io.input.max_tokens } : {}),
    ...(io.input.temperature != null ? { temperature: io.input.temperature } : {}),
    system: io.input.system,
    tools: io.input.tools,
    messages: io.input.messages,
  };
  return [
    "curl https://api.definable.ai/v1/messages \\",
    '  -H "x-api-key: $DEFINABLE_API_KEY" \\',
    '  -H "content-type: application/json" \\',
    `  -d '${JSON.stringify(body)}'`,
  ].join("\n");
}

/* --------------------------- sessions & errors ---------------------------- */

/* A session groups runs that belong to the same conversation (chat runs by
   chat title) or the same recurring job (task runs by task name). */
export function sessionsOf(runs: Run[]): Session[] {
  const map = new Map<string, Session>();
  for (const r of runs) {
    const src = r.source;
    const key = src === "chat" ? `chat:${r.sourceLabel || r.title}` : `${src}:${r.title}`;
    if (!map.has(key)) map.set(key, { key, source: src, title: src === "chat" ? r.sourceLabel || r.title : r.title, runs: [] });
    map.get(key)!.runs.push(r);
  }
  return [...map.values()];
}

// Every failed observation across an agent's runs, grouped by tool family.
// `treeOf` is each run's span tree, made from its recorded trace.
export function errorObservations(runs: Run[], treeOf: (run: Run) => Span): ErrorObservation[] {
  const out: ErrorObservation[] = [];
  for (const r of runs) {
    const root = treeOf(r);
    for (const { span } of flatten(root)) {
      if (span.status === "error" && span.children.length === 0) out.push({ run: r, span });
    }
  }
  return out;
}

/* --------------------------- one step among others --------------------------- */

/* What the step panel asks of the tree: a model call's tool calls and the steps that
   answered them, a tool's caller, what a call's context gained, whether the run was
   stopped in the middle of a step. */

/** Every step that is not the run or an attempt, in the order they ran. */
export const stepsOf = (root: Span): Span[] => flatten(root).map((r) => r.span).filter((s) => s.kind !== "agent" && s.kind !== "turn");

/** A step the run was stopped in the middle of: the trace marks it skipped, the tree draws it done. */
export const wasStopped = (span: Span): boolean => span.record?.status === "skipped";

/** The tool calls in a model call's reply. */
export const callsOf = (span: Span): ToolUseBlock[] =>
  ((span.io.output as AssistantPayload | null)?.content ?? []).filter((b): b is ToolUseBlock => b.type === "tool_use");

export const callIdOf = (span: Span): string | undefined => (span.io.input as ToolInputPayload | null)?.tool_use_id;

/** What a tool was asked to do, in its own words: a `description` argument that reads as one
    short line. A long one is content (a skill's description), so it stays an argument. */
export function purposeOf(span: Span): string | null {
  const description = (span.io.input as ToolInputPayload | null)?.arguments?.description;
  if (typeof description !== "string") return null;
  const line = description.trim();
  return line && line.length <= 140 && !line.includes("\n") ? line.replace(/\.$/, "") : null;
}

/** The tool steps that answered a model's calls, in the calls' order: by the call's id, else
    the next step of that name no other call has taken. */
export function stepsForCalls(steps: Span[], calls: ToolUseBlock[], after: Span): (Span | null)[] {
  const tools = steps.filter((s) => s.kind === "tool" && s.start >= after.start);
  const taken = new Set<string>();
  const byId = calls.map((c) => tools.find((s) => callIdOf(s) === c.id) ?? null);
  byId.forEach((s) => s && taken.add(s.id));
  return calls.map((c, i) => {
    if (byId[i]) return byId[i];
    const step = tools.find((s) => !taken.has(s.id) && s.name === c.name && !callIdOf(s)) ?? null;
    if (step) taken.add(step.id);
    return step;
  });
}

/** A tool step's caller: the model call before it whose reply asked for it, by the call's id. */
export function callerOf(span: Span, steps: Span[]): Span | null {
  const id = callIdOf(span);
  if (!id) return null;
  return [...steps].reverse().find((s) => s.kind === "llm" && s.start <= span.start && callsOf(s).some((c) => c.id === id)) ?? null;
}

/** The model call that read a failed tool's error back, if one did. */
export function errorReadBy(span: Span, steps: Span[]): Span | null {
  const id = callIdOf(span);
  if (!id) return null;
  return steps.find((s) =>
    s.kind === "llm" && s.start >= span.start
    && ((s.io.input as LlmInputPayload | null)?.messages ?? []).some((m) => m.content.some((b) => b.type === "tool_result" && b.tool_use_id === id)),
  ) ?? null;
}

/** Each model call's previous one in the same attempt (an attempt's history starts afresh). */
export function previousCalls(root: Span): Map<string, Span | null> {
  const before = new Map<string, Span | null>();
  for (const { span: holder } of flatten(root)) {
    let last: Span | null = null;
    for (const child of holder.children) {
      if (child.kind !== "llm") continue;
      before.set(child.id, last);
      last = child;
    }
  }
  return before;
}

/** Tokens the model read: the fresh ones and the ones from its cache. */
const promptTokens = (usage: TokenUsage | undefined) => (usage?.input_tokens ?? 0) + (usage?.cache_read_input_tokens ?? 0);

export interface CallContext {
  /** The attempt's first call: the whole prompt is new to it. */
  first: boolean;
  /** What came in since the previous call — tool results, a person's words; not the model's own reply. */
  fresh: ChatMessage[];
  /** The rest of what it was sent, in order. */
  earlier: ChatMessage[];
  /** The conversation's length as the run counted it. */
  length: number;
  /** Messages of the chat before this turn: counted by the run, not kept in the trace. */
  unrecorded: number;
  /** Prompt tokens gained since the previous call. */
  grew: number | null;
}

/** What a model call's context held, against its attempt's previous call. */
export function contextOf(span: Span, previous: Span | null): CallContext {
  const messages = (span.io.input as LlmInputPayload | null)?.messages ?? [];
  const before = previous ? ((previous.io.input as LlmInputPayload | null)?.messages.length ?? 0) : 0;
  const since = messages.slice(Math.min(before, messages.length));
  const fresh = since.filter((m) => m.role !== "assistant");
  const counted = (span.record?.input as { messages?: number } | null)?.messages;
  return {
    first: !previous,
    fresh,
    earlier: messages.filter((m) => !fresh.includes(m)),
    length: counted ?? messages.length,
    unrecorded: counted != null ? Math.max(0, counted - messages.length) : 0,
    grew: previous ? promptTokens((span.io.output as AssistantPayload | null)?.usage) - promptTokens((previous.io.output as AssistantPayload | null)?.usage) : null,
  };
}
